'use client';

import React from 'react';
import dynamic from 'next/dynamic';
import { JsonLd } from 'components/shared';
import {
  generateProductSchema,
  generateFAQSchema,
  generateBreadcrumbSchema,
} from 'lib/seo/schema';
import { useRegionLocale } from 'contexts/RegionLocaleContext';
import { ShopifyStore } from 'lib/commerce/shopify/shopifyHelper';
import { Currency } from 'utils/Currency';
import { HomePrice } from 'content/products/uhHome';
import { HomeFaqData } from 'components/uh-home/faq';
import { extractTextFromJSX } from 'utils/extractTextFromJSX';
import { getCompressedAssetUrl } from 'lib/config/assetUrls';
import { Banner } from 'components/landing';
import { ButtonV2 } from 'components/shared';
import styles from './home-client.module.css';
import type {
  BannerContentProps,
  BannerBackgroundProps,
} from 'components/landing/Banner';
import { BackgroundVideo } from 'components/landing/BackgroundVideo';
import { MasterTimelineProvider } from 'contexts/MasterTimelineContext';
import {
  DayAndNight,
  HIPAACompliance,
  RingWithHome,
  HomeDeviceView,
} from 'components/uh-home/v2';
import { useWindowSize } from 'hooks/dom/useWindowSize';
import { ImageTextBlockLayout } from 'components/uh-home/ImageTextBlockLayout';

const SleepFeatureCards = dynamic(
  () => import('components/uh-home/SleepFeatureCards'),
  { ssr: true },
);
// These branch on useWindowSize in render (width:0 on the server), so SSR
// would emit mobile markup/styles for everyone — keep them client-only.
const RespiratoryHealth = dynamic(
  () => import('components/uh-home/v2/RespiratoryHealth'),
  { ssr: false },
);
const ProgressTabsVideoSection = dynamic(
  () =>
    import('components/uh-home/v2/ProgressTabVideoSection').then((mod) => ({
      default: mod.ProgressTabsVideoSection,
    })),
  { ssr: false },
);
const BentoSection = dynamic(
  () =>
    import('components/uh-home/BentoSection').then((mod) => ({
      default: mod.BentoSection,
    })),
  { ssr: false },
);
const Experience = dynamic(
  () =>
    import('components/uh-home/ExperienceNew').then((mod) => ({
      default: mod.Experience,
    })),
  { ssr: true },
);
const Features = dynamic(() => import('components/uh-home/v2/Features'), {
  ssr: true,
});
const CustomBannerBackground: React.FC<BannerBackgroundProps> = ({
  background,
  selectSrc,
  resolveVideoSrc,
  resolveImageSrc,
  backgroundStyles,
}) => {
  if (!background || background.type !== 'video') return null;

  return (
    <>
      <BackgroundVideo
        src={resolveVideoSrc(selectSrc(background.src))}
        poster={resolveImageSrc(selectSrc(background.poster))}
        autoPlay={background.autoPlay ?? true}
        loop={background.loop ?? true}
        muted={background.muted ?? true}
        playsInline={background.playsInline ?? true}
        playOnce={background.playOnce ?? true}
        visibilityThreshold={background.visibilityThreshold ?? 0.1}
        showReplayButton={background.showReplayButton ?? false}
        style={backgroundStyles}
      />
      <div className={styles.gradientOverlay} aria-hidden="true" />
    </>
  );
};

const CustomBannerContent: React.FC<BannerContentProps> = ({
  title,
  subtitle,
  cta,
  ctaAlt,
}) => {
  return (
    <>
      {(title || subtitle) && (
        <div className={styles.textStack}>
          {!!title && <h1 className={styles.title}>{title}</h1>}
          {!!subtitle && <p className={styles.subtitle}>{subtitle}</p>}
        </div>
      )}

      {(cta || ctaAlt) && (
        <div className={styles.buttons}>
          {!!cta && <ButtonV2 {...cta}>{cta.children}</ButtonV2>}
          {!!ctaAlt && <ButtonV2 {...ctaAlt}>{ctaAlt.children}</ButtonV2>}
        </div>
      )}
    </>
  );
};

const HomeContent = () => {
  const { width } = useWindowSize();
  const isMobile = width <= 767;
  const { region: country, store: regionStore, currency: regionCurrency } = useRegionLocale();

  // Get country-specific pricing and currency
  const countryCode = (country || 'US').toUpperCase();
  const store = regionStore ?? ShopifyStore.ROW;
  const currency = regionCurrency ?? Currency.usd;
  const actualPrice = HomePrice[store] || 549;

  // Convert currency enum to uppercase string for schema.org (expects 'USD', 'EUR', etc.)
  const currencyString = currency.toUpperCase();

  // Generate JSON-LD schemas for Home page using actual page data
  const productSchema = generateProductSchema({
    name: 'Ultrahuman Home',
    description:
      'A home health device that monitors light, air, temperature, and noise, helping you optimise your space for better sleep, focus, and well-being.',
    imageUrl: 'https://www.ultrahuman.com/images/uh-home/uh-home-hero.png',
    brand: 'Ultrahuman',
    category: 'Home Health Monitoring',
    price: actualPrice,
    currency: currencyString,
    country: countryCode,
    availability: 'InStock',
    url: `https://www.ultrahuman.com/home/buy/${countryCode.toLowerCase()}`,
  });

  // Convert FAQ data to schema format
  const faqItems = HomeFaqData.map((faq) => ({
    question: faq.title,
    answer: extractTextFromJSX(faq.description) || 'Ultrahuman Home FAQ answer',
  })).filter((item) => item.question.trim().length > 0);

  const faqSchema = generateFAQSchema(faqItems);

  const breadcrumbSchema = generateBreadcrumbSchema([
    {
      name: 'Home',
      url: 'https://www.ultrahuman.com',
    },
    {
      name: 'Ultrahuman Home',
      url: 'https://www.ultrahuman.com/home',
    },
  ]);

  const homeSchemas = [productSchema, faqSchema, breadcrumbSchema];

  return (
    <>
      {/* Preload critical first frames for image sequences */}
      <link
        rel="preload"
        as="image"
        href={getCompressedAssetUrl(
          '/web_v2/uh-home-v2/sequences/home-device/Comp_00000.png',
        )}
      />
      <link
        rel="preload"
        as="image"
        media="(max-width: 767px)"
        href={getCompressedAssetUrl(
          '/web_v2/uh-home-v2/sequences/day-night-v8/mobile/00000.png/w_800',
        )}
      />
      <link
        rel="preload"
        as="image"
        media="(min-width: 768px)"
        href={getCompressedAssetUrl(
          '/web_v2/uh-home-v2/sequences/day-night-v8/desktop/00000.png/w_1920',
        )}
      />
      <JsonLd data={homeSchemas} />
      <Banner
        title="Ultrahuman Home"
        subtitle="A quiet yet powerful sleep monitor to help you rest better."
        cta={{ href: '/home/buy', children: 'Buy now', variant: 'white' }}
        sectionStyles={{
          backgroundColor: '#000',
          height: '100svh',
        }}
        background={
          isMobile
            ? {
                type: 'video',
                src: 'web_v2/uh-home-v2/banners/hero-final-mobile.mp4/w_1080',
                loop: false,
                playOnce: true,
                showReplayButton: true,
              }
            : {
                type: 'video',
                src: 'web_v2/uh-home-v2/banners/hero-final.mp4/w_1920',
                loop: false,
                playOnce: true,
                showReplayButton: true,
              }
        }
        position={'top'}
        contentColor="#fff"
        CustomContentComponent={CustomBannerContent}
        CustomBackgroundComponent={CustomBannerBackground}
      />
      <HomeDeviceView />
      <DayAndNight />
      <SleepFeatureCards />
      <RespiratoryHealth />
      <ProgressTabsVideoSection />
      <HIPAACompliance />
      <ImageTextBlockLayout htmlId="uh-home-image-text-block" />
      <RingWithHome />
      {/* <HomeEnvironmentBento /> */}
      <BentoSection />
      <Experience />
      <Features />
      <Banner
        title="Ultrahuman Home"
        subtitle="A quiet yet powerful sleep monitor to help you rest better."
        cta={{ href: '/home/buy', children: 'Buy now' }}
        background={{
          type: 'image',
          src: {
            mobile: 'web_v2/uh-home-v2/banners/end-banner/mobile.png',
            web: 'web_v2/uh-home-v2/banners/end-banner/desktop.png',
          },
          alt: 'Ultrahuman Home device on dark surface with blurred background of person wearing ring',
        }}
        position="center"
        contentColor="#fff"
        sectionStyles={{ height: '100vh' }}
        CustomContentComponent={CustomBannerContent}
      />
    </>
  );
};

const Home = () => {
  return (
    <MasterTimelineProvider>
      <HomeContent />
    </MasterTimelineProvider>
  );
};

export default Home;
